import { useEffect, useState } from "react";
import { DndContext, DragEndEvent } from "@dnd-kit/core";
import { useTranslation } from "react-i18next";
import { DraggableStudent } from "./DraggableStudent";
import { TimeslotDropZone } from "./TimeslotDropZone";
import { assignmentsApi, scheduleApi } from "../api/client";
import type {
  Student,
  Timeslot,
  ScheduleAssignment,
  TimeslotPreference,
  DayOfWeek,
} from "../types";
import "./Schedule.css";

interface Props {
  students: Student[];
  timeslots: Timeslot[];
  assignments: ScheduleAssignment[];
  preferences: TimeslotPreference[];
  onAssignmentsChange: (assignments: ScheduleAssignment[]) => void;
  schoolId: number;
}

const DAYS: DayOfWeek[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
];

export function Schedule({
  students,
  timeslots,
  assignments,
  preferences,
  onAssignmentsChange,
  schoolId,
}: Props) {
  const { t } = useTranslation();
  const [computing, setComputing] = useState(false);

  // On mount: restore last computed schedule for this school if present,
  // otherwise ensure no students are shown initially
  useEffect(() => {
    const storageKey = `schedule:lastComputed:${schoolId}`;
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved) as ScheduleAssignment[];
        onAssignmentsChange(Array.isArray(parsed) ? parsed : []);
      } else {
        onAssignmentsChange([]);
      }
    } catch {
      onAssignmentsChange([]);
    }
    // We only want to run this on first mount per school
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]);

  // Calendar layout constants (08:00–18:00 visible window)
  const DAY_START_MIN = 8 * 60;
  const DAY_END_MIN = 18 * 60;
  const DAY_TOTAL_MIN = DAY_END_MIN - DAY_START_MIN; // 600
  const DAY_PIXELS = 600; // height in px per day column
  const PX_PER_MIN = DAY_PIXELS / DAY_TOTAL_MIN; // 1 px per min

  // Get preferences for a specific timeslot
  const getPreferencesForTimeslot = (timeslot: Timeslot) => {
    return preferences.filter(
      (pref) =>
        pref.day === timeslot.day && pref.start_time === timeslot.start_time,
    );
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;

    const studentData = active.data.current?.student as Student;
    const timeslotData = over.data.current?.timeslot as Timeslot;

    if (studentData && timeslotData) {
      // Check if student is already assigned to this timeslot
      const existingAssignment = assignments.find(
        (a) =>
          a.student_id === studentData.id && a.timeslot_id === timeslotData.id,
      );

      if (!existingAssignment) {
        const newAssignment: ScheduleAssignment = {
          student_id: studentData.id,
          student_name: studentData.name,
          timeslot_id: timeslotData.id,
          day: timeslotData.day,
          start_time: timeslotData.start_time,
        };

        // Save to backend
        try {
          await assignmentsApi.create({
            student_id: studentData.id,
            timeslot_id: timeslotData.id,
            school_id: schoolId,
          });
          // Update local state only after successful save
          onAssignmentsChange([...assignments, newAssignment]);
        } catch (error) {
          console.error("Failed to create assignment:", error);
          alert("Failed to save assignment. Please try again.");
        }
      }
    }
  };

  const handleRemoveAssignment = async (assignment: ScheduleAssignment) => {
    try {
      await assignmentsApi.delete(
        assignment.student_id,
        assignment.timeslot_id,
      );
      // Update local state only after successful delete
      onAssignmentsChange(
        assignments.filter(
          (a) =>
            !(
              a.student_id === assignment.student_id &&
              a.timeslot_id === assignment.timeslot_id
            ),
        ),
      );
    } catch (error) {
      console.error("Failed to delete assignment:", error);
      alert("Failed to remove assignment. Please try again.");
    }
  };

  const handleClearAll = () => {
    if (window.confirm(t("schedule.confirmClear"))) {
      onAssignmentsChange([]);
      try {
        const storageKey = `schedule:lastComputed:${schoolId}`;
        localStorage.removeItem(storageKey);
      } catch {}
    }
  };

  const getTimeslotsForDay = (day: DayOfWeek) => {
    return timeslots
      .filter((t) => t.day === day)
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
  };

  const getAssignmentForTimeslot = (_timeslotId: number) => {
    // Do not render inside drop zones anymore. Calendar shows computed blocks instead.
    return undefined;
  };

  const isStudentScheduled = (studentId: number) => {
    return assignments.some((a) => a.student_id === studentId);
  };

  const handleExport = () => {
    window.print();
  };

  const handleComputeSchedule = async () => {
    setComputing(true);
    try {
      const res = await scheduleApi.compute(schoolId);
      const computed: any[] = res.data as any[]; // backend now returns computed start_time and duration
      if (!computed || computed.length === 0) {
        alert(t("schedule.noMatches"));
      }
      const assignmentsWithNames = computed.map((a) => {
        const student = students.find((s) => s.id === a.student_id);
        const timeslot = timeslots.find((t) => t.id === a.timeslot_id);
        const start =
          (a.start_time as string)?.slice(0, 5) ||
          timeslot?.start_time ||
          "00:00";
        return {
          student_id: a.student_id,
          student_name: student?.name || "Unknown",
          timeslot_id: a.timeslot_id,
          day: (timeslot?.day as DayOfWeek) || a.day || "monday",
          start_time: start,
          duration: a.duration ?? timeslot?.duration,
        } as ScheduleAssignment;
      });
      onAssignmentsChange(assignmentsWithNames);
      try {
        const storageKey = `schedule:lastComputed:${schoolId}`;
        localStorage.setItem(storageKey, JSON.stringify(assignmentsWithNames));
      } catch {}
    } catch (error) {
      console.error("Failed to compute schedule:", error);
      alert(t("common.error"));
    } finally {
      setComputing(false);
    }
  };

  return (
    <DndContext onDragEnd={handleDragEnd}>
      <div className="schedule-top-actions">
        <button
          className="btn-primary"
          onClick={handleComputeSchedule}
          disabled={computing}
        >
          {computing ? t("schedule.computing") : t("schedule.compute")}
        </button>
      </div>
      <div className="schedule-container">
        <div className="schedule-sidebar">
          <h3>{t("schedule.studentPreferences")}</h3>
          <div className="students-list">
            {students.length === 0 ? (
              <p className="empty-message">{t("students.noStudents")}</p>
            ) : (
              students.map((student) => (
                <DraggableStudent
                  key={student.id}
                  student={student}
                  isScheduled={isStudentScheduled(student.id)}
                />
              ))
            )}
          </div>
        </div>

        <div className="schedule-grid">
          {/* Left time gutter */}
          <div className="time-gutter">
            <h3 className="day-header">&nbsp;</h3>
            <div className="timeslots-list">
              {/* Global time bands to visualize teacher availability across the Y axis */}
              {timeslots.map((slot, idx) => {
                const [sh, sm] = slot.start_time.split(":").map(Number);
                const startMin = sh * 60 + sm;
                const visibleStart = Math.max(startMin, DAY_START_MIN);
                const visibleEnd = Math.min(
                  startMin + slot.duration,
                  DAY_END_MIN,
                );
                const relStart = Math.max(0, visibleStart - DAY_START_MIN);
                const top = relStart * PX_PER_MIN;
                const height = Math.max(
                  12,
                  (visibleEnd - visibleStart) * PX_PER_MIN,
                );
                return (
                  <div
                    key={`tb-${idx}`}
                    className="time-band"
                    style={{ top: `${top}px`, height: `${height}px` }}
                  />
                );
              })}
              {Array.from({
                length: (DAY_END_MIN - DAY_START_MIN) / 60 + 1,
              }).map((_, idx) => {
                const hour = 8 + idx; // matches DAY_START_MIN = 8*60
                const top = (hour * 60 - DAY_START_MIN) * PX_PER_MIN;
                const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
                return (
                  <div
                    key={`lbl-${hour}`}
                    className="time-label"
                    style={{ top: `${top}px` }}
                  >
                    {pad(hour)}:00
                  </div>
                );
              })}
            </div>
          </div>
          {DAYS.map((day) => (
            <div key={day} className="day-column">
              <h3 className="day-header">{t(`days.${day}`)}</h3>
              <div className="timeslots-list">
                {/* Background bands for teacher availability in this day */}
                {getTimeslotsForDay(day).map((slot, i) => {
                  const [sh, sm] = slot.start_time.split(":").map(Number);
                  const startMin = sh * 60 + sm;
                  const visibleStart = Math.max(startMin, DAY_START_MIN);
                  const visibleEnd = Math.min(
                    startMin + slot.duration,
                    DAY_END_MIN,
                  );
                  const relStart = Math.max(0, visibleStart - DAY_START_MIN);
                  const top = relStart * PX_PER_MIN;
                  const height = Math.max(
                    12,
                    (visibleEnd - visibleStart) * PX_PER_MIN,
                  );
                  return (
                    <div
                      key={`cb-${slot.id}-${i}`}
                      className="column-band"
                      style={{ top: `${top}px`, height: `${height}px` }}
                    />
                  );
                })}
                {getTimeslotsForDay(day).length === 0 ? (
                  <div className="empty-day">
                    <p>{t("schedule.addTimeslot")}</p>
                  </div>
                ) : (
                  <>
                    {/* Drop zones per teacher availability - positioned by time */}
                    {getTimeslotsForDay(day).map((timeslot) => {
                      const slotPreferences =
                        getPreferencesForTimeslot(timeslot);
                      const [sh, sm] = timeslot.start_time
                        .split(":")
                        .map(Number);
                      const startMin = sh * 60 + sm;
                      const relStart = Math.max(0, startMin - DAY_START_MIN);
                      const end = startMin + timeslot.duration;
                      const relEnd = Math.min(DAY_END_MIN, end) - DAY_START_MIN;
                      const top = relStart * PX_PER_MIN;
                      const height = Math.max(
                        24,
                        (relEnd - relStart) * PX_PER_MIN,
                      );
                      return (
                        <TimeslotDropZone
                          key={`dz-${timeslot.id}`}
                          timeslot={timeslot}
                          assignment={getAssignmentForTimeslot(timeslot.id)}
                          preferences={slotPreferences}
                          students={students}
                          onRemove={handleRemoveAssignment}
                          style={{ top: `${top}px`, height: `${height}px` }}
                        />
                      );
                    })}

                    {/* Render computed events as blocks */}
                    {assignments
                      .filter((a) => a.day === day)
                      .map((a, idx) => {
                        const [sh, sm] = a.start_time.split(":").map(Number);
                        const startMin = sh * 60 + sm;
                        const dur = a.duration ?? 30;
                        const visibleStart = Math.max(startMin, DAY_START_MIN);
                        const visibleEnd = Math.min(
                          startMin + dur,
                          DAY_END_MIN,
                        );
                        const relStart = Math.max(
                          0,
                          visibleStart - DAY_START_MIN,
                        );
                        const top = relStart * PX_PER_MIN;
                        const height = Math.max(
                          18,
                          (visibleEnd - visibleStart) * PX_PER_MIN,
                        );
                        const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
                        const displayStart = `${pad(sh)}:${pad(sm)}`;
                        return (
                          <div
                            key={`ev-${day}-${idx}`}
                            className="calendar-event"
                            style={{ top: `${top}px`, height: `${height}px` }}
                          >
                            <div className="title">{a.student_name}</div>
                            <div className="time">
                              {displayStart} –{" "}
                              {(() => {
                                const end = startMin + dur;
                                const eh = Math.floor(end / 60);
                                const em = end % 60;
                                return `${pad(eh)}:${pad(em)}`;
                              })()}
                            </div>
                          </div>
                        );
                      })}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {assignments.length > 0 && (
        <div className="schedule-actions">
          <button className="btn-secondary" onClick={handleClearAll}>
            {t("common.clear")}
          </button>
          <button className="btn-primary" onClick={handleExport}>
            {t("schedule.export")} ({assignments.length})
          </button>
        </div>
      )}
    </DndContext>
  );
}
